import type { DisplayConfigItem } from '../display-descriptors';
import { isAutoManagedDisplayRow } from '../pipeline-kernel';
import type { ResourceConfigEntry } from '../pipeline-kernel/types';

export interface MergeManagedDisplayRowsOptions {
  current: DisplayConfigItem[];
  fresh: DisplayConfigItem[];
  resourceConfigs: ResourceConfigEntry[];
  innerFormValues?: Record<string, unknown>;
  displayMode?: 'productCard' | 'confirm';
  excludedIds?: Set<string>;
  filterRow?: (row: DisplayConfigItem, values: Record<string, unknown>) => boolean;
}

function isManagedRow(id: string, resourceConfigs: ResourceConfigEntry[]): boolean {
  return isAutoManagedDisplayRow(id, resourceConfigs);
}

function applyRowFilters(rows: DisplayConfigItem[], options: MergeManagedDisplayRowsOptions): DisplayConfigItem[] {
  let result = rows;
  if (options.excludedIds?.size) {
    result = result.filter((row) => !options.excludedIds!.has(row.id));
  }
  if (options.filterRow && options.innerFormValues) {
    result = result.filter((row) => options.filterRow!(row, options.innerFormValues!));
  }
  return result;
}

/**
 * displayConfig 增量 merge：保留用户手填的非 managed 行，更新资源相关 managed 行。
 * productCard 首次（current 为空）仅灌入 managed 行，避免把确认预览类字段带入商品卡片。
 */
export function mergeManagedDisplayRows(options: MergeManagedDisplayRowsOptions): DisplayConfigItem[] {
  const { current, fresh, resourceConfigs } = options;
  const filteredFresh = applyRowFilters(fresh, options);

  if (options.displayMode === 'productCard' && !current.length) {
    return filteredFresh.filter((row) => isManagedRow(row.id, resourceConfigs));
  }

  const freshManagedMap = new Map(filteredFresh.filter((row) => isManagedRow(row.id, resourceConfigs)).map((row) => [row.id, row]));

  const next: DisplayConfigItem[] = [];
  const usedIds = new Set<string>();

  for (const row of current) {
    if (!isManagedRow(row.id, resourceConfigs)) {
      next.push(row);
      continue;
    }
    const freshRow = freshManagedMap.get(row.id);
    if (freshRow) {
      next.push(freshRow);
      usedIds.add(row.id);
    }
  }

  for (const row of filteredFresh) {
    if (isManagedRow(row.id, resourceConfigs) && !usedIds.has(row.id)) {
      next.push(row);
    }
  }

  // 对 current 中残留的非 managed 排除项再过滤（如历史 displayConfig 中的白名单）
  return applyRowFilters(next, options);
}
